/**
 * Reviewer-side session store.
 *
 * Holds:
 *   - reviewer identity (persisted in localStorage scoped to shareToken)
 *   - in-flight draft comments (per shareToken, persisted across reloads so a reviewer
 *     doesn't lose their work if they accidentally refresh)
 *   - submission status
 */

import { create } from 'zustand';
import type { DraftReviewComment, ReviewerIdentity } from './types';
import { submitReview } from '../../../services/reviewsAPI';

const IDENTITY_KEY = (shareToken: string) => `reviewer:${shareToken}`;
const DRAFTS_KEY = (shareToken: string) => `reviewerDrafts:${shareToken}`;

function genKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function readIdentity(shareToken: string): ReviewerIdentity | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(IDENTITY_KEY(shareToken));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.name || !parsed?.key) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeIdentity(shareToken: string, id: ReviewerIdentity) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(IDENTITY_KEY(shareToken), JSON.stringify(id));
  } catch {
    // ignore quota errors
  }
}

function readDrafts(shareToken: string): DraftReviewComment[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(DRAFTS_KEY(shareToken));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((d) => d?.localId && d?.slideId) : [];
  } catch {
    return [];
  }
}

function writeDrafts(shareToken: string, drafts: DraftReviewComment[]) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(DRAFTS_KEY(shareToken), JSON.stringify(drafts));
  } catch {
    // ignore
  }
}

interface ReviewSessionState {
  shareToken: string | null;
  identity: ReviewerIdentity | null;
  drafts: DraftReviewComment[];
  submitting: boolean;
  lastSubmittedAt: number | null;
  lastError: string | null;

  /** Hydrate from localStorage when the route mounts. */
  init(shareToken: string): void;
  /** Save identity (after the modal). */
  setIdentity(name: string, email?: string): void;
  /** Add a freshly authored comment. */
  addDraft(d: Omit<DraftReviewComment, 'localId'>): void;
  updateDraft(localId: string, text: string): void;
  removeDraft(localId: string): void;
  clearDrafts(): void;
  /** POST drafts to backend. Clears drafts on success. */
  submit(): Promise<{ reviewId: string; commentCount: number }>;
}

export const useReviewSession = create<ReviewSessionState>((set, get) => ({
  shareToken: null,
  identity: null,
  drafts: [],
  submitting: false,
  lastSubmittedAt: null,
  lastError: null,

  init(shareToken) {
    if (get().shareToken === shareToken && get().identity) return;
    set({
      shareToken,
      identity: readIdentity(shareToken),
      drafts: readDrafts(shareToken),
      submitting: false,
      lastError: null,
    });
  },

  setIdentity(name, email) {
    const shareToken = get().shareToken;
    if (!shareToken) return;
    const existing = get().identity;
    const identity: ReviewerIdentity = {
      name: name.trim(),
      email: email?.trim() || undefined,
      key: existing?.key ?? genKey(),
    };
    writeIdentity(shareToken, identity);
    set({ identity });
  },

  addDraft(d) {
    const shareToken = get().shareToken;
    if (!shareToken) return;
    const draft: DraftReviewComment = { ...d, localId: genKey() };
    const next = [...get().drafts, draft];
    writeDrafts(shareToken, next);
    set({ drafts: next });
  },

  updateDraft(localId, text) {
    const shareToken = get().shareToken;
    if (!shareToken) return;
    const next = get().drafts.map((d) => (d.localId === localId ? { ...d, text } : d));
    writeDrafts(shareToken, next);
    set({ drafts: next });
  },

  removeDraft(localId) {
    const shareToken = get().shareToken;
    if (!shareToken) return;
    const next = get().drafts.filter((d) => d.localId !== localId);
    writeDrafts(shareToken, next);
    set({ drafts: next });
  },

  clearDrafts() {
    const shareToken = get().shareToken;
    if (!shareToken) return;
    writeDrafts(shareToken, []);
    set({ drafts: [] });
  },

  async submit() {
    const { shareToken, identity, drafts } = get();
    if (!shareToken) throw new Error('Review session not initialized');
    if (!identity) throw new Error('Reviewer identity required');
    if (drafts.length === 0) throw new Error('No comments to submit');

    set({ submitting: true, lastError: null });
    try {
      const res = await submitReview(shareToken, {
        reviewer: { name: identity.name, email: identity.email ?? null, key: identity.key },
        comments: drafts.map((d) => ({
          slideId: d.slideId,
          slideIndex: d.slideIndex,
          slideTitle: d.slideTitle,
          timestamp: d.timestamp,
          text: d.text,
        })),
      });
      writeDrafts(shareToken, []);
      set({ drafts: [], submitting: false, lastSubmittedAt: Date.now() });
      return { reviewId: res.reviewId, commentCount: res.commentCount };
    } catch (err: any) {
      set({ submitting: false, lastError: err?.message ?? 'Failed to submit review' });
      throw err;
    }
  },
}));
